import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { AppStoreBadges } from './AppStoreBadges';
import { isAppleDevice } from '../Donee/components/common/utils';
import { usePushEvents } from '../../hooks/PushEvents';
import {
    clickEvent,
    successEvent,
    viewEvent,
} from '../../hooks/PushEvents/clevertapEvents';
import DeepLinkCopiedModal from '../Donee/components/CheckoutWizard/ThankYouWizard/DeepLinkCopiedModal';
import { getDoneeAmountDeepLink, useDeepLink } from '../../hooks/useDeepLink';
import { Donee, useDonorProfile, useDonee } from '../../api';
import { FeatureFlag } from '../../constants';
import { useFeatureFlag } from '../../providers';

const getSubTitle = (donee: Donee | undefined) => (
    <>
        When you open the app, tap “Allow Paste”
        {donee && (
            <>
                <br />
                to get to {donee.organization.name}
            </>
        )}
    </>
);

const appStoreUrl =
    'https://apps.apple.com/app/apple-store/id725815127?pt=8388804&ct=oga_appdownload_footer&mt=8';
const googlePlayUrl =
    'https://play.google.com/store/apps/details?id=com.pushcontrolz.givelify&referrer=utm_source%3Doga%26utm_medium%3Dweb%26utm_campaign%3Doga_appdownload_footer';

const handleIosDownload = () => {
    window.location.href = appStoreUrl;
};

const handleAndroidDownload = () => {
    window.location.href = googlePlayUrl;
};

const handleDownloadButton = () => {
    // copyDoneeIdToClipboard();
    isAppleDevice() ? handleIosDownload() : handleAndroidDownload();
};

const eventPayload = {
    name: 'FooterButton',
    location: 'Footer',
    screen: 'Gift Amount',
    type: 'Button',
    action: 'clicked',
};
const appSuccessIosModalEventPayload = {
    name: 'FooterButtonIosModal',
    location: 'Footer',
    screen: 'Gift Amount',
    action: 'completed',
    voucher: 'DDL voucher',
};
const appClickIosModalEventPayload = {
    name: 'FooterButtonIosModal',
    location: 'Footer',
    screen: 'Gift Amount',
    type: 'Button',
    action: 'clicked',
};
const appViewIosModalEventPayload = {
    name: 'FooterButtonIosModal',
    location: 'Footer',
    screen: 'Gift Amount',
    action: 'shown',
};
const appCloseIosModalEventPayload = {
    eventName: clickEvent,
    eventPayload: {
        name: 'FooterButtonIosModal',
        type: 'Button',
        location: 'Footer',
        screen: 'Gift Amount',
        action: 'dismissed',
    },
};

const StyledDiv = styled.div`
    .deep-link-loading {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

interface DownloadAppButtonsProps {
    className?: string;
}

const DownloadAppButtons: React.FC<DownloadAppButtonsProps> = ({
    className,
}) => {
    const { donee } = useDonee();
    const { pushEvent } = usePushEvents();
    const { donorProfile } = useDonorProfile();
    const { isFeatureEnabled } = useFeatureFlag();
    const [url, setUrl] = useState<string | undefined>(undefined);
    const { deepLinkState, getDeepLink, showCopiedModal, setShowCopiedModal } =
        useDeepLink(
            clickEvent,
            {
                ...eventPayload,
                action: 'success',
                voucher: 'DDL voucher copied',
            },
            handleDownloadButton,
        );

    const handleDeepLinkClick = useCallback(() => {
        getDeepLink(
            donee,
            donorProfile,
            getDoneeAmountDeepLink(donee),
            undefined,
        ).then((s) => {
            s && setUrl(s);
        });
    }, [donee, donorProfile, getDeepLink]);

    const handleClick = useCallback(
        (platform: 'android' | 'ios') => {
            isFeatureEnabled(FeatureFlag.OGA_APPFOOTERDEEPLINKS)
                ? handleDeepLinkClick()
                : platform === 'ios'
                  ? handleIosDownload()
                  : handleAndroidDownload();
        },
        [isFeatureEnabled, handleDeepLinkClick],
    );

    const handleAndroid = useCallback(() => {
        pushEvent(clickEvent, {
            ...eventPayload,
            platform: 'android',
            'oga-appfooterdeeplinks': isFeatureEnabled(
                FeatureFlag.OGA_APPFOOTERDEEPLINKS,
            ),
        });
        handleClick('android');
    }, [pushEvent, isFeatureEnabled, handleClick]);

    const handleIos = useCallback(() => {
        pushEvent(clickEvent, {
            ...eventPayload,
            'oga-appfooterdeeplinks': isFeatureEnabled(
                FeatureFlag.OGA_APPFOOTERDEEPLINKS,
            ),
        });
        handleClick('ios');
    }, [pushEvent, isFeatureEnabled, handleClick]);

    const isLoading = deepLinkState.loading;

    return (
        <>
            <StyledDiv className={className}>
                <AppStoreBadges
                    onIosClick={handleIos}
                    onAndroidClick={handleAndroid}
                    isLoading={isLoading}
                />
            </StyledDiv>
            <DeepLinkCopiedModal
                url={url}
                open={showCopiedModal}
                title="Get the free Givelify app"
                subTitle={getSubTitle(donee)}
                viewEvent={viewEvent}
                clickEvent={clickEvent}
                successEvent={successEvent}
                viewEventPayload={appViewIosModalEventPayload}
                clickEventPayload={appClickIosModalEventPayload}
                successEventPayload={appSuccessIosModalEventPayload}
                closeEventPayload={appCloseIosModalEventPayload}
                onClick={handleDownloadButton}
                onClose={() => setShowCopiedModal(false)}
            />
        </>
    );
};

export default DownloadAppButtons;
